import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Directive, inject, Input } from '@angular/core';
import { tuiInjectElement } from '@taiga-ui/cdk/utils/dom';
import { tuiWithStyles, tuiCreateToken, tuiProvideOptions } from '@taiga-ui/cdk/utils/miscellaneous';
import { toSignal } from '@angular/core/rxjs-interop';
import { ResizeObserverService } from '@ng-web-apis/resize-observer';
import { tuiZonefull, tuiWatch } from '@taiga-ui/cdk/observables';
import { BehaviorSubject, combineLatest, map, distinctUntilChanged, of, delay } from 'rxjs';
const _c0 = ["tuiProgressBar", ""];
const _c1 = ["tuiProgressLabel", ""];
const _c2 = [[["progress"]], [["tui-progress-circle"]], "*"];
const _c3 = ["progress", "tui-progress-circle", "*"];
let TuiProgressFixedGradientStyles = /*#__PURE__*/(() => {
  class TuiProgressFixedGradientStyles {
    static {
      this.ɵfac = function TuiProgressFixedGradientStyles_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressFixedGradientStyles)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiProgressFixedGradientStyles,
        selectors: [["ng-component"]],
        hostAttrs: [1, "tui-fixed-gradient"],
        decls: 0,
        vars: 0,
        template: function TuiProgressFixedGradientStyles_Template(rf, ctx) {},
        styles: ["[tuiProgressFixedGradient]::-moz-progress-bar{inline-size:100%!important;clip-path:inset(0 calc(100% - var(--tui-progress-percent)) 0 0 round var(--tui-radius-m));-moz-transition:clip-path var(--tui-duration) linear;transition:clip-path var(--tui-duration) linear;margin-right:calc(-100% + var(--tui-progress-percent))}[tuiProgressFixedGradient]::-webkit-progress-value{inline-size:100%!important;clip-path:inset(0 calc(100% - var(--tui-progress-percent)) 0 0 round var(--tui-radius-m));-webkit-transition:clip-path var(--tui-duration) linear;transition:clip-path var(--tui-duration) linear}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiProgressFixedGradientStyles;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiProgressFixedGradientDirective = /*#__PURE__*/(() => {
  class TuiProgressFixedGradientDirective {
    constructor() {
      this.nativeProgress = tuiInjectElement();
      this.nothing = tuiWithStyles(TuiProgressFixedGradientStyles);
    }
    get progressPercent() {
      const {
        value
      } = this.nativeProgress;
      const max = this.nativeProgress.max ?? 1;
      return Math.min(value / max * 100, 100);
    }
    static {
      this.ɵfac = function TuiProgressFixedGradientDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressFixedGradientDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiProgressFixedGradientDirective,
        selectors: [["progress", "tuiProgressBar", "", "tuiProgressFixedGradient", ""]],
        hostVars: 2,
        hostBindings: function TuiProgressFixedGradientDirective_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵstyleProp("--tui-progress-percent", ctx.progressPercent, "%");
          }
        }
      });
    }
  }
  return TuiProgressFixedGradientDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TUI_PROGRESS_DEFAULT_OPTIONS = {
  color: null,
  size: 'm'
};
const TUI_PROGRESS_OPTIONS = tuiCreateToken(TUI_PROGRESS_DEFAULT_OPTIONS);
function tuiProgressOptionsProvider(options) {
  return tuiProvideOptions(TUI_PROGRESS_OPTIONS, options, TUI_PROGRESS_DEFAULT_OPTIONS);
}
let TuiProgressBar = /*#__PURE__*/(() => {
  class TuiProgressBar {
    constructor() {
      this.options = inject(TUI_PROGRESS_OPTIONS);
      this.color = this.options.color;
      this.size = this.options.size;
    }
    static {
      this.ɵfac = function TuiProgressBar_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressBar)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiProgressBar,
        selectors: [["progress", "tuiProgressBar", ""]],
        hostVars: 3,
        hostBindings: function TuiProgressBar_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-size", ctx.size);
            i0.ɵɵstyleProp("--tui-progress-color", ctx.color);
          }
        },
        inputs: {
          color: "color",
          size: "size"
        },
        attrs: _c0,
        decls: 0,
        vars: 0,
        template: function TuiProgressBar_Template(rf, ctx) {},
        styles: ["@keyframes tuiIndeterminateAnimation{50%{background-position:left}}[tuiProgressBar]{-webkit-appearance:none;appearance:none;border:none;--t-height: .75rem;display:block;inline-size:100%;block-size:var(--t-height);color:var(--tui-background-accent-1);background:var(--tui-background-neutral-2);clip-path:inset(0 .5px round var(--tui-radius-m));overflow:hidden;border-radius:1rem;flex-shrink:0}[tuiProgressBar]::-webkit-progress-value{-webkit-transition:width var(--tui-duration) linear;transition:width var(--tui-duration) linear}[tuiProgressBar]::-webkit-progress-value{background:var(--tui-progress-color, currentColor);border-radius:inherit}[tuiProgressBar]::-moz-progress-bar{background:var(--tui-progress-color, currentColor);border-radius:inherit}[tuiProgressBar][data-size=xxs]{--t-height: .125rem}[tuiProgressBar][data-size=xs]{--t-height: .25rem}[tuiProgressBar][data-size=s]{--t-height: .5rem}[tuiProgressBar][data-size=l]{--t-height: 1rem}[tuiProgressBar][data-size=xl]{--t-height: 1.25rem}[tuiProgressBar][data-size=xxl]{--t-height: 1.5rem}[tuiProgressBar]:indeterminate{background:linear-gradient(to right,var(--tui-background-neutral-2) 0 45%,var(--tui-progress-color, currentColor) 45% 55%,var(--tui-background-neutral-2) 55% 100%) right;background-size:225%;animation:tuiIndeterminateAnimation 3s infinite ease-in-out}[tuiProgressBar]:indeterminate::-webkit-progress-value{background:transparent}[tuiProgressBar]:indeterminate::-moz-progress-bar{background:transparent}[tuiProgressBar]::-webkit-progress-inner-element{border-radius:inherit}[tuiProgressBar]::-webkit-progress-bar{background:transparent;border-radius:inherit}label[tuiProgressLabel] [tuiProgressBar]:not(:first-child){position:absolute;top:0;left:0;inline-size:100%;block-size:100%;background:transparent}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiProgressBar;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiProgressColorSegments = /*#__PURE__*/(() => {
  class TuiProgressColorSegments {
    constructor() {
      this.colors$ = new BehaviorSubject([]);
      this.el = tuiInjectElement();
      this.color = toSignal(combineLatest([this.colors$, inject(ResizeObserverService, {
        self: true
      }).pipe(map(() => this.el.offsetWidth), distinctUntilChanged())]).pipe(map(([colors, width]) => {
        const segmentWidth = Math.ceil(width / colors.length);
        const colorsString = colors.reduce((acc, color, i) => `${acc}, ${color} ${i * segmentWidth}px ${(i + 1) * segmentWidth}px`, '');
        return `linear-gradient(to right ${colorsString})`;
      }), tuiZonefull(), tuiWatch()));
    }
    set colors(colors) {
      this.colors$.next(colors);
    }
    static {
      this.ɵfac = function TuiProgressColorSegments_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressColorSegments)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiProgressColorSegments,
        selectors: [["progress", "tuiProgressBar", "", "tuiProgressColorSegments", ""]],
        hostVars: 2,
        hostBindings: function TuiProgressColorSegments_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵstyleProp("--tui-progress-color", ctx.color());
          }
        },
        inputs: {
          colors: [0, "tuiProgressColorSegments", "colors"]
        },
        features: [i0.ɵɵProvidersFeature([ResizeObserverService])]
      });
    }
  }
  return TuiProgressColorSegments;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiProgressCircle = /*#__PURE__*/(() => {
  class TuiProgressCircle {
    constructor() {
      this.options = inject(TUI_PROGRESS_OPTIONS);
      this.animationDelay = toSignal(of(true).pipe(delay(0)));
      this.value = 0;
      this.max = 1;
      this.color = this.options.color;
      this.size = this.options.size;
    }
    get progressRatio() {
      const ratio = this.value / this.max;
      return Number.isFinite(ratio) ? ratio : 0;
    }
    static {
      this.ɵfac = function TuiProgressCircle_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressCircle)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiProgressCircle,
        selectors: [["tui-progress-circle"]],
        hostVars: 5,
        hostBindings: function TuiProgressCircle_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-size", ctx.size);
            i0.ɵɵstyleProp("--tui-progress-color", ctx.color)("--t-progress-ratio", ctx.progressRatio);
          }
        },
        inputs: {
          value: "value",
          max: "max",
          color: "color",
          size: "size"
        },
        decls: 4,
        vars: 4,
        consts: [[1, "t-hidden-progress", 3, "max", "value"], ["aria-hidden", "true", "height", "100%", "width", "100%", 1, "t-svg"], ["cx", "50%", "cy", "50%", 1, "t-track"], ["cx", "50%", "cy", "50%", 1, "t-progress"]],
        template: function TuiProgressCircle_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "progress", 0);
            i0.ɵɵnamespaceSVG();
            i0.ɵɵelementStart(1, "svg", 1);
            i0.ɵɵelement(2, "circle", 2)(3, "circle", 3);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵproperty("max", ctx.max)("value", ctx.value);
            i0.ɵɵadvance(3);
            i0.ɵɵclassProp("t-progress_filled", ctx.animationDelay());
          }
        },
        styles: ["[_nghost-%COMP%]{--t-track-stroke: var(--tui-thickness, .375em);--t-progress-stroke: var(--tui-thickness, .375em);position:relative;display:block;color:var(--tui-background-accent-1);transform:rotate(-90deg);transform-origin:center;font-size:1rem;inline-size:var(--t-diameter);block-size:var(--t-diameter);border-radius:100%;-webkit-mask:radial-gradient(closest-side,transparent calc(100% - var(--t-track-stroke)),#000 calc(100% - var(--t-track-stroke) + .5px));mask:radial-gradient(closest-side,transparent calc(100% - var(--t-track-stroke)),#000 calc(100% - var(--t-track-stroke) + .5px))}[data-size=xxs][_nghost-%COMP%]{--t-diameter: 2em}[data-size=xs][_nghost-%COMP%]{--t-diameter: 2.5em}[data-size=s][_nghost-%COMP%]{--t-diameter: 3.5em}[data-size=m][_nghost-%COMP%]{--t-diameter: 4em}[data-size=l][_nghost-%COMP%]{--t-diameter: 5em}[data-size=xl][_nghost-%COMP%]{--t-diameter: 6em}[data-size=xxl][_nghost-%COMP%]{--t-diameter: 8em}.t-track[_ngcontent-%COMP%]{fill:transparent;stroke:var(--tui-background-neutral-1);stroke-width:var(--t-track-stroke);r:calc((var(--t-diameter) - var(--t-track-stroke)) / 2)}.t-progress[_ngcontent-%COMP%]{fill:transparent;stroke:var(--tui-progress-color, currentColor);stroke-linecap:round;stroke-width:var(--t-progress-stroke);stroke-dasharray:calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2));stroke-dashoffset:calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2));r:calc((var(--t-diameter) - var(--t-progress-stroke)) / 2)}.t-progress_filled[_ngcontent-%COMP%]{transition:stroke-dashoffset var(--tui-duration) linear;stroke-dashoffset:calc(calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2)) - var(--t-progress-ratio) * calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2)))}.t-hidden-progress[_ngcontent-%COMP%]{position:absolute;clip:rect(1px,1px,1px,1px);clip-path:inset(50%);block-size:1px;inline-size:1px;margin:-1px;overflow:hidden;padding:0}.t-svg[_ngcontent-%COMP%]{overflow:unset}"],
        changeDetection: 0
      });
    }
  }
  return TuiProgressCircle;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiProgressLabel = /*#__PURE__*/(() => {
  class TuiProgressLabel {
    static {
      this.ɵfac = function TuiProgressLabel_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressLabel)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiProgressLabel,
        selectors: [["label", "tuiProgressLabel", ""]],
        attrs: _c1,
        ngContentSelectors: _c3,
        decls: 4,
        vars: 0,
        consts: [[1, "t-label"]],
        template: function TuiProgressLabel_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c2);
            i0.ɵɵprojection(0);
            i0.ɵɵprojection(1, 1);
            i0.ɵɵelementStart(2, "span", 0);
            i0.ɵɵprojection(3, 2);
            i0.ɵɵelementEnd();
          }
        },
        styles: ["[_nghost-%COMP%]{position:relative;display:inline-block;color:var(--tui-text-primary)}.t-label[_ngcontent-%COMP%]{position:absolute;top:0;left:0;inline-size:100%;block-size:100%;display:flex;font:var(--tui-font-text-s);flex-direction:column;justify-content:center;align-items:center}"],
        changeDetection: 0
      });
    }
  }
  return TuiProgressLabel;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiProgressSegmentedStyles = /*#__PURE__*/(() => {
  class TuiProgressSegmentedStyles {
    static {
      this.ɵfac = function TuiProgressSegmentedStyles_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressSegmentedStyles)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiProgressSegmentedStyles,
        selectors: [["ng-component"]],
        hostAttrs: [1, "tui-progress-segmented"],
        decls: 0,
        vars: 0,
        template: function TuiProgressSegmentedStyles_Template(rf, ctx) {},
        styles: ["[tuiProgressBar]._segmented{--tui-segment-gap: .5rem;-webkit-mask-image:radial-gradient(circle closest-side at calc(var(--t-height) / 2) center,#999 0 99%,transparent calc(99% + .6px) 100%),radial-gradient(circle closest-side at calc(100% - calc(var(--t-height) / 2) - var(--tui-segment-gap)) center,#999 0 99%,transparent calc(99% + .6px) 100%),linear-gradient(to right,transparent 0 calc(var(--t-height) / 2),#999 calc(var(--t-height) / 2) calc(100% - calc(var(--t-height) / 2) - var(--tui-segment-gap)),transparent calc(100% - calc(var(--t-height) / 2) - var(--tui-segment-gap)));mask-image:radial-gradient(circle closest-side at calc(var(--t-height) / 2) center,#999 0 99%,transparent calc(99% + .6px) 100%),radial-gradient(circle closest-side at calc(100% - calc(var(--t-height) / 2) - var(--tui-segment-gap)) center,#999 0 99%,transparent calc(99% + .6px) 100%),linear-gradient(to right,transparent 0 calc(var(--t-height) / 2),#999 calc(var(--t-height) / 2) calc(100% - calc(var(--t-height) / 2) - var(--tui-segment-gap)),transparent calc(100% - calc(var(--t-height) / 2) - var(--tui-segment-gap)));-webkit-mask-size:calc(100% * var(--t-segment-width) + var(--tui-segment-gap) * var(--t-segment-width));mask-size:calc(100% * var(--t-segment-width) + var(--tui-segment-gap) * var(--t-segment-width))}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiProgressSegmentedStyles;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiProgressSegmented = /*#__PURE__*/(() => {
  class TuiProgressSegmented {
    constructor() {
      this.nothing = tuiWithStyles(TuiProgressSegmentedStyles);
      this.segments = 1;
    }
    static {
      this.ɵfac = function TuiProgressSegmented_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiProgressSegmented)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiProgressSegmented,
        selectors: [["", "tuiProgressBar", "", "segments", ""]],
        hostAttrs: [1, "_segmented"],
        hostVars: 2,
        hostBindings: function TuiProgressSegmented_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵstyleProp("--t-segment-width", 1 / ctx.segments);
          }
        },
        inputs: {
          segments: "segments"
        }
      });
    }
  }
  return TuiProgressSegmented;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TuiProgress = [TuiProgressBar, TuiProgressCircle, TuiProgressColorSegments, TuiProgressFixedGradientDirective, TuiProgressLabel, TuiProgressSegmented];

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_PROGRESS_DEFAULT_OPTIONS, TUI_PROGRESS_OPTIONS, TuiProgress, TuiProgressBar, TuiProgressCircle, TuiProgressColorSegments, TuiProgressFixedGradientDirective, TuiProgressLabel, TuiProgressSegmented, tuiProgressOptionsProvider };
